<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
    <div class="max-w-4xl mx-auto">
      <!-- ✅ Review Display -->
      <div v-if="!loading" class="bg-white rounded-2xl shadow-md p-6">
        <!-- Review Header -->
        <div class="flex items-start gap-4 mb-4">
          <div
              class="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold">
            {{ getInitials(review.full_name) }}
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2 mb-1">
              <h3 class="font-semibold text-gray-900">{{ review.full_name }}</h3>
              <span class="text-sm text-gray-500">{{ review.created_at }}</span>
              <button @click="toggleActive(review)"
                      :class="review.active ? 'text-white bg-green-600 rounded-full px-2 py-1 font-medium' : 'text-white bg-red-600 rounded-full px-2 py-1 font-medium'">
                {{ review.active ? 'Active' : 'Inactive' }}
              </button>
            </div>
            <!-- Stars -->
            <div class="flex items-center gap-1 mb-1">
              <svg
                  v-for="star in 5"
                  :key="'star-' + star"
                  class="w-5 h-5"
                  fill="currentColor"
                  :class="star <= review.rate ? 'text-orange-600' : 'text-gray-300'"
                  viewBox="0 0 20 20"
              >
                <path
                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                />
              </svg>
            </div>
            <p class="font-medium text-gray-900">{{ review.title }}</p>
          </div>
        </div>

        <p class="text-gray-700 mb-4">{{ review.description }}</p>

        <!-- Review Images -->
        <div v-if="review.images" class="flex flex-wrap flex-shrink-0 gap-2 my-4">

          <img
              v-for="image in review.images"
              :src="image.url"
              :key="image.id"
              class="mt-2 w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition"
              @click="openImage(image.url)"
              alt="Reply attachment"
          />

          <!-- Modal -->
          <transition name="fade">
            <div
                v-if="isImageOpen"
                class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
                @click.self="closeImage"
            >
              <img
                  :src="activeImage"
                  class="max-w-full max-h-[75%] rounded-lg shadow-lg"
              />
              <!-- Close button -->
              <button
                  @click="closeImage"
                  class="absolute top-4 right-4 text-white text-2xl font-bold"
              >
                ✕
              </button>
            </div>
          </transition>
        </div>
        <!-- End Of Review Images -->

        <!-- ✅ Replies -->
        <div v-if="review.replies && review.replies.length > 0" class="space-y-3">
          <div class="flex items-center gap-1">
            <h4 class="text-sm font-semibold text-gray-900">
              Replies ({{ review.replies.length }})
            </h4>
          </div>

          <div
              v-for="reply in review.replies"
              :key="reply.id"
              :class="[
              'rounded-xl p-4 border-2',
              reply.is_admin ? 'bg-orange-50 border-orange-600' : 'bg-gray-50 border-gray-200'
            ]"
          >
            <div class="flex items-start gap-3">
              <!-- Avatar -->
              <div
                  :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold',
                  reply.is_admin ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700'
                ]"
              >
                {{ getInitials(reply.full_name) }}
              </div>

              <div class="flex-1">
                <div class="flex items-center gap-2 flex-wrap">
                  <p
                      :class="[
                      'font-semibold text-sm',
                      reply.is_admin ? 'text-orange-600' : 'text-gray-900'
                    ]"
                  >
                    {{ reply.full_name }}
                  </p>

                  <span
                      v-if="reply.is_admin"
                      class="text-xs bg-orange-600 text-white px-2 py-0.5 rounded-full"
                  >
                    Admin
                  </span>

                  <button
                      @click="toggleReplyActive(reply.id)"
                      :disabled="activeLoading"
                      :class="[
                      'text-xs px-2 py-0.5 rounded-full font-medium',
                      reply.active
                        ? 'bg-green-100 text-green-700 border border-green-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-300'
                    ]"
                  >
                    {{ reply.active ? 'Active' : 'Inactive' }}
                  </button>
                </div>

                <!-- ✅ Inline Edit or Message -->
                <div class="mt-2 w-full">
                  <template v-if="!reply.isEditing">

                    <p class="text-gray-700 text-sm">{{ reply.message }}</p>

                    <!-- Image -->
                    <div v-if="reply.images" class="flex flex-wrap flex-shrink-0 gap-2">

                      <img
                          v-for="image in reply.images"
                          :src="image.url"
                          :key="image.id"
                          class="mt-2 w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition"
                          @click="openImage(image.url)"
                          alt="Reply attachment"
                      />
                    </div>

                    <div class="flex gap-2 mt-3">
                      <Button
                          label="Edit"
                          class="bg-white text-black text-xs px-2 py-0.5 rounded-full font-medium"
                          size="small"
                          text
                          @click="reply.isEditing = true"
                      />
                      <Button
                          label="Delete"
                          class="bg-white text-red-600 text-xs px-2 py-0.5 rounded-full font-medium"
                          size="small"
                          text
                          @click="deleteReply(reply.id)"
                      />
                    </div>
                  </template>

                  <!-- Edit Mode -->
                  <template v-else>
          <textarea
              v-model="reply.message"
              class="w-full border rounded-lg p-2 text-sm mt-1"
              placeholder="Edit your reply message..."
          ></textarea>


                    <div class="flex gap-2 mt-3">
                      <Button label="Save" size="small" @click="saveEdit(reply)"/>
                      <Button
                          label="Cancel"
                          size="small"
                          severity="secondary"
                          @click="reply.isEditing = false"
                      />
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Add Reply -->
        <div class="mt-6 text-center text-gray-500 text-sm">
          <Button
              severity="contrast"
              @click="showReplyModal"
              class="text-sm px-4 py-1 rounded-full font-medium hover:bg-gray-800 transition-all"
          >
            Add Reply
          </Button>
        </div>
      </div>

      <!-- ✅ Loading State -->
      <div v-else class="bg-white rounded-2xl shadow-md p-12 text-center">
        <div class="animate-spin h-8 w-8 border-4 border-orange-600 border-t-transparent rounded-full mx-auto"></div>
        <p class="text-gray-500 mt-4">Loading review...</p>
      </div>
    </div>

    <!-- ✅ Image Modal -->
    <transition name="fade">
      <div
          v-if="isImageOpen"
          class="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          @click.self="closeImage"
      >
        <img
            :src="activeImage"
            class="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
            alt="Full size image"
        />
        <button
            @click="closeImage"
            class="absolute top-4 right-4 text-white text-3xl font-bold hover:text-gray-300 transition"
        >
          ✕
        </button>
      </div>
    </transition>
  </div>

  <ReplyModal @reload="reloadReview" v-if="!loading" v-model:isModalOpen="isModalOpen" :review_id="review.id"
              :key="review.id"/>
</template>

<script setup>
import {ref, onMounted, watch} from 'vue'
import store from '@/store'
import {useRoute} from 'vue-router'
import {useToast} from 'primevue/usetoast'
import {Button} from 'primevue'
import ReplyModal from '@/components/ReplyModal.vue'

const toast = useToast()
const route = useRoute()
const review = ref(null)
const loading = ref(true)
const activeLoading = ref(false)
const isModalOpen = ref(false)
const showSuccess = (detail) => {
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail,
    life: 3000
  })
}

function reloadReview() {
  getReview()
}

function toggleReplyActive(reply_id) {
  activeLoading.value = true
  store
      .dispatch('toggleReplyActive', reply_id)
      .then(() => {
        showSuccess('Reply status updated successfully')
        getReview()
      })
      .catch((err) => console.log(err))
      .finally(() => {
        activeLoading.value = false
      })
}

function showReplyModal() {
  isModalOpen.value = true
}

function toggleActive(review) {
  store.dispatch('toggleReviewActive', review.id)
      .then(() => {
        review.active = !review.active
        showSuccess('Review Active Status Updated Successfully')
      })
      .catch(err => {
        console.log(err)
      })
}



// ✅ Edit / Save Reply
function saveEdit(reply) {
  store.dispatch('updateReply', reply)
      .then(() => {
        reply.isEditing = false
        getReview()
        showSuccess('Reply updated successfully')
      })
      .catch((err) => console.log(err))
}


function deleteReply(replyId) {
  if (confirm('Are you sure you want to delete this reply?')) {
    store
        .dispatch('deleteReply', replyId)
        .then(() => {
          review.value.replies = review.value.replies.filter((r) => r.id !== replyId)
          showSuccess('Reply deleted successfully')
        })
        .catch((err) => console.log(err))
  }
}

// ✅ Image Modal
const isImageOpen = ref(false)
const activeImage = ref(null)
const openImage = (url) => {
  activeImage.value = url
  isImageOpen.value = true
}
const closeImage = () => {
  isImageOpen.value = false
  activeImage.value = null
}


const getInitials = (name) =>
    name
        ? name
            .split(' ')
            .map((word) => word[0])
            .join('')
            .toUpperCase()
        : ''


const getReview = () => {
  loading.value = true
  store
      .dispatch('getReview', route.params.id)
      .then(({data}) => {
        review.value = data
        // Add isEditing = false for each reply
        review.value.replies?.forEach((r) => (r.isEditing = false))
      })
      .catch(() => {
        // Mock fallback
        review.value = {
          id: route.params.id,
          full_name: 'Sarah Johnson',
          title: 'Amazing quality and service',
          rate: 5,
          description:
              "Absolutely amazing product! The quality exceeded my expectations. I've been using it for a month now and couldn’t be happier with my purchase.",
          created_at: '2 days ago',
          replies: [
            {
              id: 101,
              full_name: 'Admin Team',
              is_admin: true,
              message:
                  "Thank you Sarah! We're thrilled you had a positive experience with our product.",
              url: null,
              isEditing: false
            },
            {
              id: 102,
              full_name: 'John Doe',
              is_admin: false,
              message: 'I agree! I had the same experience with this product.',
              url: null,
              isEditing: false
            }
          ]
        }
      })
      .finally(() => {
        loading.value = false
      })
}

watch(
    () => route.params.id,
    (newId, oldId) => {
      if (newId !== oldId) {
        getReview() // re-fetch new review
      }
    }
)

onMounted(() => {
  getReview()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
